import { minBy } from "lodash-es";

import type { MappedServiceData } from "@/features/flights/components/flight-purchase/AdditionalServicesSelection/types";
import type { AdditionalServiceData } from "@/features/flights/types/types";
import { getTravelerPassengerTypeCode } from "@/features/travellers/utils/transformTraveler";
import type { GetFlightAvailabilityTokenAdditionalService } from "@/types/codegen";
import { type Traveller } from "@/types/types";
import { SUPPORTED_ADDITIONAL_SERVICES_CODES } from "@/utils/constants";

type GetAvailableServicesParams = {
  traveller: Traveller;
  travellerIndex: number | null;
  optionalServices: GetFlightAvailabilityTokenAdditionalService[] | undefined;
  mandatoryServices: GetFlightAvailabilityTokenAdditionalService[] | undefined;
};

export const getAvailableServices = ({
  traveller,
  travellerIndex,
  optionalServices,
  mandatoryServices,
}: GetAvailableServicesParams) => {
  return [
    ...(optionalServices || []).map((service) => ({
      service,
      mandatory: false,
    })),
    ...(mandatoryServices || []).map((service) => ({
      service,
      mandatory: true,
    })),
  ].filter(
    ({ service }) =>
      SUPPORTED_ADDITIONAL_SERVICES_CODES.includes(
        service.service.categoryCode,
      ) &&
      !service.included &&
      Number(service.passengerIndex) === travellerIndex &&
      service.target === getTravelerPassengerTypeCode(traveller),
  );
};

export const getCheapestMandatoryServices = (services: MappedServiceData[]) => {
  return services.reduce((acc, { service, mandatory }) => {
    if (!mandatory) {
      return acc;
    }

    const cheapestOption = minBy(
      service.options,
      (option) => option.price.amount,
    );

    if (!cheapestOption) {
      return acc;
    }

    return [
      ...acc,
      {
        service: {
          bookingCode: service.bookingCode,
          selectedOptionCode: cheapestOption.code,
        },
        mandatory,
      },
    ];
  }, [] as AdditionalServiceData[]);
};
